import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management/";
import { ITeamsIdMap, TeamsActions } from "@gtmhub/teams";
import { IOrganization, IOrganizationTeamMapping } from "@gtmhub/configuration/workday/models";
import { OrganizationsActions } from "@gtmhub/configuration/workday/redux/organizations-actions";
import { StateService } from "@uirouter/angularjs";
import { Unsubscribe } from "redux";

import { WorkdayUserProvisioningService } from "@gtmhub/configuration/workday/workday-user-provisioning-service";
import { localize } from "@gtmhub/localization";
import { IComponentOptions } from "angular";

export class OrganizationTeamMappingsCtrl {
  indicators: {
    loading?: IIndicator;
  };

  teamsFetched: boolean;
  organizationsFetched: boolean;
  reduxError: IUIError;
  organizationTeamMappings: IOrganizationTeamMapping[] = [];
  organizations: IOrganization[];
  teams: ITeamsIdMap;
  searchOrganizationsPlaceholder: string;
  searchTerm: string;
  private unsubscribeFromStore: Unsubscribe;

  static $inject = ["$scope", "$state", "WorkdayUserProvisioningService", "UIErrorHandlingService", "$ngRedux", "TeamsActions", "OrganizationsActions"];
  constructor(
    private $scope: IExtendedRootScopeService,
    private $state: StateService,
    private workdayUserProvisioningService: WorkdayUserProvisioningService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private organizationsActions: OrganizationsActions
  ) {}

  $onInit(): void {
    this.indicators = {
      loading: { progress: true },
    };
    this.searchTerm = "";
    this.searchOrganizationsPlaceholder = localize("search_organizations");

    const bindStateToScope = (state) => {
      return {
        organizations: state.organizations.organizations,
        organizationsFetched: state.organizations.isFetched,
        teams: state.teams.map,
        teamsFetched: state.teams.isFetched,
      };
    };
    this.unsubscribeFromStore = this.$ngRedux.connect(bindStateToScope.bind(this.$state))(this);
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.$ngRedux.dispatch(this.organizationsActions.fetchOrganizationsIfMissing());
    this.$scope
      .watchUntilAndRejectOnError(
        () => this.organizationsFetched && this.teamsFetched,
        () => this.reduxError
      )
      .then(() => {
        delete this.indicators.loading;
        this.workdayUserProvisioningService.mapTeamsToOrganizationTeamMappings(this.teams, this.organizationTeamMappings);
        this.getOrganizationTeamMappings();
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
        delete this.indicators.loading;
      });

    this.$scope.$on("mappingSaved", (en, mapping: IOrganizationTeamMapping, isMappingDeleted: boolean) => this.updateSavedMapping(mapping, isMappingDeleted));
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
  }

  getOrganizationTeamMappings(): void {
    this.indicators.loading = { progress: true };
    this.workdayUserProvisioningService.getOrganizationTeamMapping().then(
      (response) => {
        this.workdayUserProvisioningService.mapTeamAndOrganizationNameToId(response.gtmhubToWorkdayMappings, this.organizationTeamMappings, this.organizations);
        delete this.indicators.loading;
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        delete this.indicators.loading;
      }
    );
  }
  resetIndicator(indicator: keyof OrganizationTeamMappingsCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  removeTeamMapping(teamId: string, organizationId: string): void {
    if (organizationId) {
      this.workdayUserProvisioningService
        .deleteTeamToOrganizationMapping(teamId)
        .then(() => {
          const deletedMapping = this.organizationTeamMappings.find((organizationTeamMapping) => organizationTeamMapping.gtmhubTeamId === teamId);
          if (deletedMapping) {
            deletedMapping.workdayOrganizationId = "";
            deletedMapping.workdayOrganizationName = "";
          }
          this.$scope.$root.$broadcast("mappingUpdated");
        })
        .catch((error) => {
          this.uiErrorHandlingService.handleModal(error);
        });
    }
  }
  private updateSavedMapping(mapping: IOrganizationTeamMapping, isMappingDeleted: boolean): void {
    if (isMappingDeleted) {
      const deletedMapping = this.organizationTeamMappings.find(
        (organizationTeamMapping) => organizationTeamMapping.workdayOrganizationId === mapping.workdayOrganizationId
      );
      deletedMapping.workdayOrganizationId = "";
      deletedMapping.workdayOrganizationName = "";
    }
    const organizationTeamMapping = this.organizationTeamMappings.find((organizationTeamMapping) => organizationTeamMapping.gtmhubTeamId === mapping.gtmhubTeamId);
    organizationTeamMapping.workdayOrganizationId = mapping.workdayOrganizationId;
    this.workdayUserProvisioningService.mapOrganizationIdToOrganizationName(organizationTeamMapping, this.organizations);
    this.$scope.$root.$broadcast("mappingUpdated");
  }
}

export const OrganizationTeamMappingsComponent: IComponentOptions = {
  controller: OrganizationTeamMappingsCtrl,
  templateUrl: require("./organization-team-mappings.jade"),
};
